
import { SearchAndSelect } from '../../..';
import { Box, Chip, CircularProgress, InputAdornment, ListItemButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useField } from 'formik';
import { getTreatDrugs } from '@uplift/api';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from '@mui/icons-material';

const name = 'drugs';
const searchName = 'drug-search';

type AutocompleteDrugOption = {
  name: string;
  id: string;
};

const useStyles = makeStyles()(() => ({
  input: {
    '&::placeholder': {
      fontStyle: 'italic'
    }
  },
  chipRoot: {
    borderRadius: 'unset'
  },
}));

export const MedicationsForm: React.VFC = () => {
  const { classes } = useStyles();
  const [ field, _meta, helpers ] = useField<string[]>(name);
  const [ searchField, _searchMeta, searchHelpers ] = useField<string>(searchName);
  const text = useMemo(() => searchField.value ?? '', [ searchField.value ]);

  const [ suggestions, setSuggestions ] = useState<AutocompleteDrugOption[]>([]);
  const [ loading, setLoading ] = useState(false);

  const fetchSuggestions = useCallback(async (text: string) => {
    const res = await getTreatDrugs(text);

    return res.data.map((drug): AutocompleteDrugOption => ({
      name: drug.name,
      id: drug.ndcCode
    }));
  }, []);

  const debouncedFetch = useMemo(() => debounce((text: string) => {
    fetchSuggestions(text).then((suggestions) => {
      setSuggestions(suggestions);
    }).catch(() => {
      setSuggestions([]);
    }).finally(() => {
      setLoading(false);
    });
  }, 300), [ fetchSuggestions ]);

  useEffect(() => {
    if (text.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debouncedFetch(text);
  }, [ text, debouncedFetch ]);

  const onAddValue = (drugNameToAdd: string) => {
    helpers.setValue([ ...field.value, drugNameToAdd ]);
    searchHelpers.setValue('');
  };

  const onDeleteValue = (drugNameToDelete: string) => {
    helpers.setValue(field.value.filter(drugName => drugName !== drugNameToDelete));
  };

  return (
    <Box>
      <Box>
        {field.value.map((drugName) => {

          return (
            <Box mb={2} key={drugName}>
              <Chip
                label={drugName}
                variant="outlined"
                classes={{ root: classes.chipRoot }}
                onDelete={() => onDeleteValue(drugName)}
              />
            </Box>
          );
        })}
      </Box>
      <SearchAndSelect
        color="secondary"
        fullWidth
        id={name}
        name={searchName}
        label="Add Self-Reported Medication"
        options={suggestions}
        popperConstraint={suggestions.length !== 0 && !loading}
        listProps={{ disablePadding: true }}
        inputClasses={{ input: classes.input }}
        renderListItem={(option) => (
          <ListItemButton onClick={() => onAddValue(option.name)}>
            {option.name}
          </ListItemButton>
        )}
        placeholder="Search medications"
        startAdornment={<InputAdornment position="start"><Search /></InputAdornment>}
        endAdornment={loading && <InputAdornment position="end"><CircularProgress color="secondary" size="24px" /></InputAdornment>}
      />
    </Box>
  );
};
